import { css } from 'lit';

/*
 * Figma token map:
 *   Colors/Background/bg-primary         -> --asc-bg-primary
 *   Colors/Background/bg-disabled_subtle -> --asc-bg-disabled_subtle
 *   Colors/Border/border-primary         -> --asc-border-primary
 *   Colors/Border/border-brand           -> --asc-border-brand         (focused)
 *   Colors/Border/border-error           -> --asc-border-error         (destructive focused)
 *   Colors/Border/border-error_subtle    -> --asc-border-error_subtle  (destructive idle)
 *   Colors/Border/border-disabled        -> --asc-border-disabled
 *   Colors/Text/text-primary             -> --asc-text-primary
 *   Colors/Text/text-secondary           -> --asc-text-secondary
 *   Colors/Text/text-tertiary            -> --asc-text-tertiary
 *   Colors/Text/text-disabled            -> --asc-text-disabled
 *   Colors/Text/text-placeholder         -> --asc-text-placeholder
 *   Colors/Text/text-brand-tertiary      -> --asc-text-brand-tertiary  (required asterisk)
 *   Colors/Text/text-error-primary       -> --asc-text-error-primary
 *   Colors/Effects/Shadows/shadow-xs     -> --asc-shadow-xs
 *   radius-md                            -> --asc-radius-md  (field wrapper: 12px)
 *   radius-sm                            -> --asc-radius-sm  (chip: 8px)
 *   spacing-xxs=2px xs=4px sm=6px md=8px lg=12px
 */

export const styles = css`
  :host {
    display: block;
    font-family: var(--asc-font-family-body);
  }

  [hidden] {
    display: none !important;
  }

  /* Base: flex column, gap=spacing-sm(6px) between label/field/footer
     Figma: flex flex-col gap-[spacing-sm] */
  [part='base'] {
    display: flex;
    flex-direction: column;
    gap: var(--asc-textarea-input-label-gap, var(--asc-spacing-sm));
  }

  /* Label row: Figma flex gap-[spacing-xxs=2px] items-center */
  [part='label-row'] {
    display: flex;
    align-items: center;
    gap: var(--asc-spacing-xxs);
    cursor: default;
  }

  /* Figma: text-sm/Medium (14px, 500, 20px lh), text-secondary (#30383b) */
  [part='label-text'] {
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-textarea-input-label-font-size, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-medium);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-textarea-input-label-color, var(--asc-text-secondary));
    white-space: nowrap;
  }

  /* Figma: text-brand-tertiary (#008577) for the required asterisk */
  [part='required-indicator'] {
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-font-size-text-sm);
    font-weight: var(--asc-font-weight-medium);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-textarea-input-required-color, var(--asc-text-brand-tertiary));
    white-space: nowrap;
  }

  /* Field wrapper
     Figma default: bg-primary, 1px border-primary, radius-md(12px),
       shadow-xs, px-[14px](no-token) py-[spacing-lg=12px]
     Figma tags:   same but p-[spacing-lg] all sides
     Focus:        border-brand + inset shadow to simulate border-2 without layout shift
     Destructive idle:    border-error_subtle (subtle pale red)
     Destructive focused: border-error (full red) + inset shadow */
  [part='field-wrapper'] {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--asc-textarea-input-bg, var(--asc-bg-primary));
    border: var(--asc-textarea-input-border-width, var(--asc-border-width)) solid
      var(--asc-textarea-input-border-color, var(--asc-border-primary));
    border-radius: var(--asc-textarea-input-border-radius, var(--asc-radius-md));
    box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs);
    /* Figma px-[14px]: no spacing token at 14px (between spacing-lg=12 and spacing-xl=16) */
    padding: var(--asc-textarea-input-padding-y, var(--asc-spacing-lg))
      var(--asc-textarea-input-padding-x, 14px); /* no-token: Figma 14px horizontal */
    min-height: var(--asc-textarea-input-min-height, 96px);
    box-sizing: border-box;
    position: relative;
    transition:
      border-color var(--asc-transition-fast),
      box-shadow var(--asc-transition-fast);
  }

  /* Figma tags mode: p-[spacing-lg=12px] all sides */
  :host([mode='tags']) [part='field-wrapper'] {
    padding: var(--asc-textarea-input-padding-y, var(--asc-spacing-lg));
  }

  /* Tags mode focused: restore all-equal padding (all sides 12px then -1px for 2px border) */
  :host([mode='tags']) [part='field-wrapper']:focus-within {
    padding: calc(var(--asc-textarea-input-padding-y, var(--asc-spacing-lg)) - 1px);
  }

  :host([mode='tags'][disabled]) [part='field-wrapper']:focus-within {
    padding: var(--asc-textarea-input-padding-y, var(--asc-spacing-lg));
  }

  :host([mode='tags'][destructive]) [part='field-wrapper']:focus-within {
    padding: calc(var(--asc-textarea-input-padding-y, var(--asc-spacing-lg)) - 1px);
  }

  /* Figma focused: border-2 border-brand */
  [part='field-wrapper']:focus-within {
    border-width: 2px;
    border-color: var(--asc-textarea-input-border-color-focus, var(--asc-border-brand));
    padding: calc(var(--asc-textarea-input-padding-y, var(--asc-spacing-lg)) - 1px)
      calc(var(--asc-textarea-input-padding-x, 14px) - 1px); /* compensate 1px border growth */
    box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs);
  }

  /* Figma destructive idle: border-error_subtle (#f69785) */
  :host([destructive]) [part='field-wrapper'] {
    border-color: var(
      --asc-textarea-input-border-color-destructive,
      var(--asc-border-error_subtle)
    );
  }

  /* Figma destructive focused: border-2 border-error (#d2260f) */
  :host([destructive]) [part='field-wrapper']:focus-within {
    border-width: 2px;
    border-color: var(
      --asc-textarea-input-border-color-destructive-focus,
      var(--asc-border-error)
    );
    padding: calc(var(--asc-textarea-input-padding-y, var(--asc-spacing-lg)) - 1px)
      calc(var(--asc-textarea-input-padding-x, 14px) - 1px); /* compensate 1px border growth */
    box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs);
  }

  /* Figma disabled: bg-disabled_subtle (#d3dadd), border-disabled (#a5b8bf) */
  :host([disabled]) [part='field-wrapper'] {
    background: var(--asc-textarea-input-bg-disabled, var(--asc-bg-disabled_subtle));
    border-color: var(--asc-textarea-input-border-color-disabled, var(--asc-border-disabled));
    cursor: not-allowed;
  }

  :host([disabled]) [part='field-wrapper']:focus-within {
    border-width: 1px;
    border-color: var(--asc-textarea-input-border-color-disabled, var(--asc-border-disabled));
    padding: var(--asc-textarea-input-padding-y, var(--asc-spacing-lg))
      var(--asc-textarea-input-padding-x, 14px); /* reset to default padding — no focus style on disabled */
    box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs);
  }

  /* Textarea: Figma text-md/Regular (16px, 400, 24px lh), text-primary */
  [part='textarea'] {
    display: block;
    flex: 1;
    min-height: 0; /* allow flex to size below intrinsic rows height */
    width: 100%;
    padding: 0;
    margin: 0;
    border: none;
    outline: none;
    background: transparent;
    resize: vertical;
    color: var(--asc-textarea-input-text-color, var(--asc-text-primary));
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-textarea-input-font-size, var(--asc-font-size-text-md));
    font-weight: var(--asc-textarea-input-font-weight, var(--asc-font-weight-regular));
    line-height: var(--asc-textarea-input-line-height, var(--asc-line-height-text-md));
    box-sizing: border-box;
  }

  [part='textarea']::placeholder {
    color: var(--asc-textarea-input-placeholder-color, var(--asc-text-placeholder));
  }

  :host([disabled]) [part='textarea'] {
    color: var(--asc-textarea-input-text-color-disabled, var(--asc-text-disabled));
    cursor: not-allowed;
  }

  /* Chips area: Figma flex flex-wrap gap-[spacing-md=8px] items-start */
  [part='chips-area'] {
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    align-items: center;
    gap: var(--asc-textarea-input-chip-gap, var(--asc-spacing-md));
    width: 100%;
    flex: 1;
    cursor: text;
  }

  /* Chip: Figma bg-primary, border-primary, radius-sm(8px),
            pl-[9px] pr-[spacing-xs=4px] py-[spacing-xxs=2px], gap-[3px] */
  [part='chip'] {
    display: inline-flex;
    align-items: center;
    gap: 3px; /* no-token: Figma gap-[3px] between chip label and remove button */
    background: var(--asc-textarea-input-chip-bg, var(--asc-bg-primary));
    border: 1px solid var(--asc-textarea-input-chip-border-color, var(--asc-border-primary));
    border-radius: var(--asc-textarea-input-chip-border-radius, var(--asc-radius-sm));
    padding-top: var(--asc-textarea-input-chip-padding-y, var(--asc-spacing-xxs));
    padding-bottom: var(--asc-textarea-input-chip-padding-y, var(--asc-spacing-xxs));
    padding-left: var(
      --asc-textarea-input-chip-padding-start,
      9px
    ); /* no-token: Figma pl-[9px], between spacing-md(8) and spacing-lg(12) */
    padding-right: var(--asc-textarea-input-chip-padding-end, var(--asc-spacing-xs));
    flex-shrink: 0;
  }

  /* Chip label: Figma text-sm/Medium (14px, 500, 20px lh), text-secondary */
  [part='chip-label'] {
    color: var(--asc-textarea-input-chip-text-color, var(--asc-text-secondary));
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-textarea-input-chip-font-size, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-medium);
    line-height: var(--asc-line-height-text-sm);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }

  /* Chip remove: Figma 16x16 total (12px icon + 2px padding each side), rounded 3px */
  [part='chip-remove'] {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 16px;  /* 12px icon + 2px padding × 2 sides, border-box */
    height: 16px; /* 12px icon + 2px padding × 2 sides, border-box */
    padding: var(--asc-spacing-xxs); /* 2px */
    border: none;
    background: none;
    cursor: pointer;
    color: var(--asc-textarea-input-chip-remove-color, var(--asc-fg-tertiary));
    border-radius: 3px; /* no-token: Figma chip remove uses 3px, not in radius scale */
    box-sizing: border-box;
    transition: color var(--asc-transition-fast);
  }

  [part='chip-remove']:hover {
    color: var(--asc-textarea-input-chip-remove-color-hover, var(--asc-fg-primary));
  }

  [part='chip-remove']:focus-visible {
    outline: 2px solid var(--asc-focus-ring);
    outline-offset: 1px;
  }

  /* Inline input: Figma text-md/Regular, text-placeholder */
  [part='inline-input'] {
    flex: 1;
    min-width: 60px; /* no-token: minimum usable typing area */
    padding: 0;
    margin: 0;
    border: none;
    outline: none;
    background: transparent;
    color: var(--asc-textarea-input-text-color, var(--asc-text-primary));
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-textarea-input-font-size, var(--asc-font-size-text-md));
    font-weight: var(--asc-textarea-input-font-weight, var(--asc-font-weight-regular));
    line-height: var(--asc-textarea-input-line-height, var(--asc-line-height-text-md));
  }

  [part='inline-input']::placeholder {
    color: var(--asc-textarea-input-placeholder-color, var(--asc-text-placeholder));
  }

  /* Footer */
  [part='footer'] {
    display: flex;
    flex-direction: column;
  }

  /* Figma hint: text-sm/Regular (14px, 400, 20px lh), text-tertiary (#49565a) */
  [part='hint'] {
    color: var(--asc-textarea-input-hint-color, var(--asc-text-tertiary));
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-textarea-input-hint-font-size, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-regular);
    line-height: var(--asc-line-height-text-sm);
  }

  /* Figma error: text-sm/Regular (14px, 400, 20px lh), text-error-primary (#d2260f) */
  [part='error'] {
    color: var(--asc-textarea-input-error-color, var(--asc-text-error-primary));
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-textarea-input-error-font-size, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-regular);
    line-height: var(--asc-line-height-text-sm);
  }
`;
